import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { MUTATIONS, useMutation } from "../../../api";
import type { WorkshopId } from "../../../api/workshop";
import Button from "../../../design-system/components/Button/Button.jsx";
import { Variants } from "../../../design-system/components/Button/Variants.jsx";
import Dialog from "../../../design-system/components/Dialog/Dialog.jsx";
import Textarea from "../../../design-system/components/Textarea/Textarea.jsx";
import Text from "../../../design-system/primitives/components/Text/Text";
import styles from "./classroomManagement.css";

/** How long the "Nachricht gesendet" confirmation stays up before auto-closing. */
const SENT_CONFIRMATION_MS = 2000;

/**
 * Compose dialog for a one-off broadcast to the class.
 *
 * A message is fire-and-forget — there's nothing to manage or end afterwards —
 * so on send the button flips to a green "gesendet" state and the dialog closes
 * itself shortly after. Closing always clears the draft, so every opening starts
 * a fresh one-off — there's no "active" message to re-open.
 *
 * Acts via the `manageWorkshop` mutation; students receive it over SSE.
 */
export const ClassMessageDialog = ({
  workshopId,
  open,
  setOpen,
}: {
  workshopId: WorkshopId;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const [text, setText] = useState("");
  const [sent, setSent] = useState(false);
  const [manageWorkshop] = useMutation(MUTATIONS.manageWorkshop);

  const close = useCallback(() => {
    setSent(false);
    setText("");
    setOpen(false);
  }, [setOpen]);

  // The "gesendet" confirmation sits for a moment, then the dialog closes itself.
  useEffect(() => {
    if (!sent) return;
    const timer = setTimeout(close, SENT_CONFIRMATION_MS);
    return () => clearTimeout(timer);
  }, [sent, close]);

  const sendMessage = () => {
    const content = text.trim();
    if (!content) return;
    void manageWorkshop({
      workshopId,
      action: { action: "showMessage", payload: { content } },
    });
    // Fire-and-forget: show the confirmation; the effect above closes the dialog.
    setSent(true);
  };

  return (
    <Dialog
      open={open}
      setOpen={close}
      title="Nachricht an die Klasse"
      buttons={[
        // On send the button itself flips to a green success state; the dialog
        // then auto-closes (see the effect above), so there's nothing to end.
        sent ? (
          <Button key="send" variant={Variants.SUCCESS} onClick={close}>
            Nachricht gesendet
          </Button>
        ) : (
          <Button
            key="send"
            variant={Variants.PRIMARY}
            onClick={sendMessage}
            disabled={text.trim() === ""}
          >
            An alle senden
          </Button>
        ),
      ]}
    >
      <div className={styles.messageStack}>
        <Text variant="body" color="neutral-100">
          Deine Nachricht erscheint auf allen Bildschirmen der Schüler*innen.
        </Text>
        <Textarea
          name="classroom-message"
          label="Nachricht"
          theme="dark"
          value={text}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setText(e.target.value)
          }
          width100
          placeholder="z. B. Super gemacht! Ihr habt noch 5 Minuten für die Recherche."
          fullHeight={false}
        />
      </div>
    </Dialog>
  );
};
